import { computed, inject, Injectable, Signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LanguageService } from '../../../../core/services/language.service';
import { IFilter, IMenu, ITablePagination, IToolbar } from '@tecalisdev/infinity';
import { MenuItemAction } from '../../../../shared/models/master.models';
import { Permission } from '../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../core/services/role-permissions.service';
import { ICompaniesListBody } from '../models/companies.models';
import { createDateRangeFilters } from '../../../../shared/list/create-date-range-filters.util';
import { buildCrudStatusMenu } from '../../../../shared/utils/build-crud-status-menu.util';

@Injectable({
	providedIn: 'root',
})
export class CompaniesHelperService {
	private trans = inject(TranslateService);
	private rolePermissions = inject(RolePermissionsService);
	private langSvc = inject(LanguageService);

	readonly toolbar: Signal<IToolbar> = computed<IToolbar>(() => {
		this.langSvc.currentLang(); // dependencia reactiva: recalcula al cambiar idioma
		const sortOptions = [
			{
				id: 'desc',
				label: this.trans.instant('COMPANIES.SORT.NEWEST_FIRST'),
				value: 'desc',
			},
			{
				id: 'asc',
				label: this.trans.instant('COMPANIES.SORT.OLDEST_FIRST'),
				value: 'asc',
			},
		];
		const filters: IFilter[] = [
			...createDateRangeFilters({
				fromTitle: this.trans.instant('FILTERS.FROM'),
				toTitle: this.trans.instant('FILTERS.TO'),
				names: { from: 'created_from', to: 'created_to' },
			}),
			{
				type: 'select',
				title: this.trans.instant('COMPANIES.SORT.LABEL'),
				name: 'sort_order',
				placeholder: this.trans.instant('COMPANIES.SORT.LABEL'),
				labelChip: this.trans.instant('COMPANIES.SORT.LABEL'),
				options: sortOptions,
				value: sortOptions[0],
			},
		];

		return {
			filters,
			fastFilters: [
				{
					name: 'enabled',
					options: [
						{ label: this.trans.instant('COMPANIES.ACTIVE'), id: 'true' },
						{ label: this.trans.instant('COMPANIES.INACTIVE'), id: 'false' },
					],
					value: 'true',
				},
			],
			search: {
				placeholder: this.trans.instant('COMPANIES.SEARCH'),
				name: 'name',
			},
			...(this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION)
				? {
						action: {
							id: MenuItemAction.SHOW_CURL,
							icon: 'icon-code-brackets',
							tooltip: this.trans.instant('COMPANIES.LIST.CURL_LIST_TOOLTIP'),
						},
					}
				: {}),
		};
	});

	get config(): { pagination: ITablePagination } {
		return {
			pagination: {
				page: 1,
				limit: 25,
				total: 0,
				label: this.trans.instant('TABLE.ELEMENTS_BY_PAGE'),
				elementsByPage: [
					{ id: '25', label: '25' },
					{ id: '50', label: '50' },
					{ id: '100', label: '100' },
				],
			},
		};
	}

	public getDetailMenuOpt(company: ICompaniesListBody): IMenu[] {
		return buildCrudStatusMenu({
			activeValue: company?.enabled,
			statusLabel: this.trans.instant('USERS.MENU.ACTIVE'),
			editLabel: this.trans.instant('BUTTON.EDIT'),
			deleteLabel: this.trans.instant('BUTTON.DELETE'),
			canToggleStatus: this.rolePermissions.canEditCompany(),
			canEdit: this.rolePermissions.canEditCompany(),
			canDelete: this.rolePermissions.canDeleteCompany(),
		});
	}
}
